import {
  Box,
  Button,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { CollapseChevron } from "components/CollapseChevron";
import { isFederalGeneratorDiscrepancy } from "encamp-shared/src/facility/generatorThresholdComparison";
import {
  federalGeneratorStatusRank,
  federalWasteGeneratorStatusToLabel,
  stateGeneratorStatusRank,
  stateWasteGeneratorStatusToLabel,
} from "encamp-shared/src/waste";
import { GeneratorStatusExceedanceDetailQuery } from "generated-graphql/graphql";
import { DateTime } from "luxon";
import { useId, useState } from "react";
import { Link } from "react-router-dom";

import { ComparisonArrow } from "./ComparisonArrow";
import { DrawerDisclosure } from "./DrawerDisclosure";
import { drawerSectionSx, DrawerSectionHeading } from "./DrawerSection";
import { calculatedStatusLabel, comparisonDescription } from "./generatorStatusDisplay";
import { affectedMonthPeriods, EvidenceMonth } from "./monthlyEvidence";
import { formatPeriodMonth, formatPeriodSpan } from "./periodFormat";

type Detail = GeneratorStatusExceedanceDetailQuery["generatorStatusExceedanceDetail"];
type RelatedDiscrepancy = Detail["relatedDiscrepancies"][number];
type EvidenceRow = { periodStart: string; month: EvidenceMonth | undefined };

/** Missing calendar months stay explicit so a gap cannot look like a sustained run. */
function evidenceRows(
  months: EvidenceMonth[],
  periodStart: string,
  lastPeriodStart: string
): EvidenceRow[] {
  const byMonth = new Map(months.map((month) => [month.periodStart, month]));
  const start = DateTime.fromISO(periodStart, { zone: "utc" }).startOf("month");
  const rows: EvidenceRow[] = [];
  for (
    let date = DateTime.fromISO(lastPeriodStart, { zone: "utc" }).startOf("month");
    date >= start;
    date = date.minus({ months: 1 })
  ) {
    const period = date.toISODate();
    if (period) rows.push({ periodStart: period, month: byMonth.get(period) });
  }
  return rows;
}

function ReportedStatus({ month, showState }: { month: EvidenceMonth; showState: boolean }) {
  const federal = month.reportedFederalStatus
    ? stateWasteGeneratorStatusToLabel(month.reportedFederalStatus)
    : "Unavailable";
  const state = month.reportedStateStatus
    ? stateWasteGeneratorStatusToLabel(month.reportedStateStatus)
    : "Unavailable";
  return (
    <Tooltip title="Reported status from RCRAInfo for this month.">
      <Box>
        {!showState || federal === state ? (
          federal
        ) : (
          <>
            <Typography variant="body2">Federal: {federal}</Typography>
            <Typography variant="body2">State: {state}</Typography>
          </>
        )}
      </Box>
    </Tooltip>
  );
}

export function CalculatedStatusValue({
  reported,
  calculated,
  rank,
}: {
  reported: string | null | undefined;
  calculated: string | null | undefined;
  rank: (status: string | null) => number;
}) {
  const comparison = comparisonDescription(reported, calculated, rank, undefined, true);
  const description =
    comparison.relation && reported
      ? `${comparison.relation === "ABOVE" ? "Above" : "Below"} reported ${stateWasteGeneratorStatusToLabel(reported)}`
      : comparison.label;
  return (
    <Tooltip title={description} describeChild>
      <Typography
        component="span"
        variant="body2"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
      >
        {calculatedStatusLabel(calculated, true)}
        <ComparisonArrow relation={comparison.relation} />
        {comparison.relation && (
          <Box component="span" sx={visuallyHidden}>
            {` — ${description}`}
          </Box>
        )}
      </Typography>
    </Tooltip>
  );
}

function EvidenceTableRow({
  row,
  inReview,
  showStateColumns,
  onSelectMonth,
  selectedMonth,
}: {
  row: EvidenceRow;
  inReview: boolean;
  showStateColumns: boolean;
  onSelectMonth?: (month: string) => void;
  selectedMonth?: string;
}) {
  const { month, periodStart } = row;
  return (
    <TableRow
      selected={selectedMonth === periodStart}
      sx={{ bgcolor: inReview ? "action.hover" : undefined }}
    >
      <TableCell
        component="th"
        scope="row"
        sx={{ fontWeight: inReview ? 600 : 400, whiteSpace: "nowrap" }}
      >
        {onSelectMonth ? (
          <Button
            size="small"
            onClick={() => onSelectMonth(periodStart)}
            aria-pressed={selectedMonth === periodStart}
            sx={{ p: 0, textTransform: "none", fontWeight: "inherit" }}
          >
            {formatPeriodMonth(periodStart)}
          </Button>
        ) : (
          formatPeriodMonth(periodStart)
        )}
        {inReview && (
          <Box component="span" sx={visuallyHidden}>
            {" "}
            — included in this review
          </Box>
        )}
      </TableCell>
      {month ? (
        <>
          <TableCell>
            <ReportedStatus month={month} showState={showStateColumns} />
          </TableCell>
          <TableCell>
            <CalculatedStatusValue
              reported={month.reportedFederalStatus}
              calculated={month.federalStatus}
              rank={federalGeneratorStatusRank}
            />
          </TableCell>
          {showStateColumns && (
            <TableCell>
              <CalculatedStatusValue
                reported={month.reportedStateStatus}
                calculated={month.stateStatus}
                rank={stateGeneratorStatusRank}
              />
            </TableCell>
          )}
        </>
      ) : (
        <TableCell colSpan={showStateColumns ? 3 : 2} sx={{ color: "text.secondary" }}>
          No calculated data for this month
        </TableCell>
      )}
    </TableRow>
  );
}

export function RelatedFindings({
  findings,
  facilityState,
  siblingDrawerSearch,
}: {
  findings: RelatedDiscrepancy[];
  facilityState: string | null;
  siblingDrawerSearch: (periodStart: string) => string;
}) {
  if (!findings.length) return null;
  return (
    <Box>
      <DrawerDisclosure title={`Other reviews for this facility (${findings.length})`}>
        <Stack component="ul" spacing={1.5} sx={{ m: 0, pl: 2 }}>
          {findings.map((finding) => (
            <Box component="li" key={finding.periodStart}>
              <MuiLink
                component={Link}
                to={{ search: siblingDrawerSearch(finding.periodStart) }}
                variant="body2"
              >
                {formatPeriodSpan(finding)}
              </MuiLink>
              {isFederalGeneratorDiscrepancy({ ...finding, state: facilityState }) && (
                <Typography variant="body2" color="text.secondary">
                  Federal:{" "}
                  {
                    comparisonDescription(
                      finding.reportedFederalStatus,
                      finding.calculatedFederalStatus,
                      federalGeneratorStatusRank,
                      undefined,
                      true
                    ).label
                  }
                  {" · Reported "}
                  {federalWasteGeneratorStatusToLabel(finding.reportedFederalStatus ?? null)}
                  {" / Calculated "}
                  {finding.calculatedFederalStatus
                    ? federalWasteGeneratorStatusToLabel(finding.calculatedFederalStatus)
                    : calculatedStatusLabel(finding.calculatedFederalStatus, true)}
                </Typography>
              )}
              {finding.stateDirection && (
                <Typography variant="body2" color="text.secondary">
                  {facilityState} state:{" "}
                  {
                    comparisonDescription(
                      finding.reportedStateStatus,
                      finding.calculatedStateStatus,
                      stateGeneratorStatusRank,
                      finding.stateDirection
                    ).label
                  }
                  {" · Reported "}
                  {stateWasteGeneratorStatusToLabel(finding.reportedStateStatus ?? null)}
                  {" / Calculated "}
                  {calculatedStatusLabel(finding.calculatedStateStatus, true)}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      </DrawerDisclosure>
    </Box>
  );
}

export function CalculatedStatusHistory({
  months,
  periodStart,
  lastPeriodStart,
  facilityState,
  showStateColumns,
  mismatchPeriodStarts,
  mismatchMonths,
  onSelectMonth,
  selectedMonth,
}: {
  months: EvidenceMonth[];
  mismatchPeriodStarts?: readonly string[] | null;
  mismatchMonths?: number | null;
  onSelectMonth?: (month: string) => void;
  selectedMonth?: string;
  periodStart: string;
  lastPeriodStart: string;
  facilityState: string | null;
  showStateColumns: boolean;
}) {
  const [showHistory, setShowHistory] = useState(false);
  const tableId = useId();
  const affected = affectedMonthPeriods({ months, mismatchPeriodStarts, mismatchMonths });
  const dates = [
    periodStart,
    lastPeriodStart,
    ...affected,
    ...months.map((month) => month.periodStart),
  ].sort();
  const first = showHistory ? (dates[0] ?? periodStart) : ([...affected].sort()[0] ?? periodStart);
  const last = showHistory ? (dates.at(-1) ?? lastPeriodStart) : lastPeriodStart;
  const periodRows = evidenceRows(months, first, last);
  const rows = showHistory ? periodRows : periodRows.filter((row) => affected.has(row.periodStart));
  const hasOtherMonths =
    months.length > rows.length || showHistory || periodRows.length > rows.length;
  const count = affected.size || mismatchMonths || 0;
  const title = showHistory ? "Full monthly history" : "Affected months";
  return (
    <Box sx={drawerSectionSx}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        gap={1}
        flexWrap="wrap"
      >
        <DrawerSectionHeading component="h3">{title}</DrawerSectionHeading>
        <Typography variant="body2" color="text.secondary">
          {count} {count === 1 ? "month" : "months"} in this review
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {showHistory
          ? "Shaded months belong to this review. Other months provide context. "
          : "Reported status at the time of each month. "}
        {onSelectMonth && "Select a month to see the data used below."}
      </Typography>
      {!rows.length && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          {mismatchMonths === 0
            ? "No affected months remain in the current data."
            : "Affected month details are unavailable. Open the full monthly history for context."}
        </Typography>
      )}
      <TableContainer
        id={tableId}
        tabIndex={0}
        role="region"
        aria-label={title}
        sx={{ maxHeight: 340 }}
      >
        <Table size="small" stickyHeader aria-label={title}>
          <TableHead>
            <TableRow>
              <TableCell>Month</TableCell>
              <TableCell>Reported status</TableCell>
              <TableCell>Calculated federal</TableCell>
              {showStateColumns && (
                <TableCell>
                  <Tooltip title={facilityState ? `${facilityState} state status` : "State status"}>
                    <span>Calculated state</span>
                  </Tooltip>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <EvidenceTableRow
                key={row.periodStart}
                row={row}
                inReview={affected.has(row.periodStart)}
                showStateColumns={showStateColumns}
                onSelectMonth={onSelectMonth}
                selectedMonth={selectedMonth}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {hasOtherMonths && (
        <Button
          size="small"
          aria-expanded={showHistory}
          aria-controls={tableId}
          onClick={() => setShowHistory((value) => !value)}
          startIcon={<CollapseChevron collapsed={!showHistory} />}
          sx={{ mt: 1, textTransform: "none" }}
        >
          {showHistory ? "Show affected months only" : "View full monthly history"}
        </Button>
      )}
    </Box>
  );
}
